import { useEffect } from 'react';

import { NON_TEXT_INPUT_TYPES } from 'lib/constants/NON_TEXT_INPUT_TYPES';

// A <form> with its own explicit submit control owns Enter. A bare <form> wrapper doesn't — the
// shared Input blocks its native submit, and the preventDefault below stops it either way. An
// expanded combobox (react-select, pickers) uses Enter to pick its highlighted option.
const isSubmittableTextInput = (el) =>
  el?.tagName === 'INPUT' &&
  !NON_TEXT_INPUT_TYPES.includes(el.type) &&
  !el.form?.querySelector('[type="submit"]') &&
  el.getAttribute('aria-expanded') !== 'true';

const isInteractive = (el) => {
  const tag = el?.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    tag === 'BUTTON' ||
    tag === 'A' ||
    el?.isContentEditable ||
    el?.getAttribute('role') === 'button'
  );
};

/**
 * Fires the primary footer action when the user presses Enter inside an open
 * modal, unless focus is on an interactive element (textarea, select, button,
 * link or any contentEditable node). Mirrors the Button component's own
 * guards so Enter can't bypass loading/disabled states.
 *
 * @param {Object} params
 * @param {boolean} params.isOpen - Controlled open state
 * @param {boolean} [params.isFooter] - Whether the footer is rendered
 * @param {Object} [params.button] - The primary footer action config
 * @param {React.RefObject<HTMLElement>} [params.containerRef] - Only react while focus is
 *   inside this element, so a modal stacked on top doesn't also fire the one below it
 * @param {boolean} [params.allowTextInputs] - Also submit from single-line text inputs
 */

const useModalEnterKey = ({
  isOpen,
  isFooter = true,
  button,
  containerRef,
  allowTextInputs = false
}) => {
  useEffect(() => {
    if (!isOpen || !isFooter || typeof button?.onClick !== 'function') return;

    const handleKeyDown = (e) => {
      if (e.key !== 'Enter') return;
      if (e.repeat) return;
      if (e.isComposing) return;
      if (e.defaultPrevented) return;
      if (button.isLoading || button.isDisabled) return;

      const el = document.activeElement;

      if (containerRef && !containerRef.current?.contains(el)) return;

      const isTextSubmit = allowTextInputs && isSubmittableTextInput(el);
      if (!isTextSubmit && isInteractive(el)) return;

      e.preventDefault();
      button.onClick();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isFooter, button, containerRef, allowTextInputs]);
};

export default useModalEnterKey;
